import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastService } from '@app/services';
import { ACTIVATION_CODE_RETRY_TIME, FormUtils, MessageType } from '@app/shared';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { CustomerService } from 'app/core/services/customer.service';
import { ActivationCodeData } from 'app/core/utils';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

@UntilDestroy()
@Component({
  selector: 'app-finalize-signup-dialog',
  templateUrl: './finalize-signup-dialog.component.html',
  styleUrls: ['./finalize-signup-dialog.component.scss'],
})
export class FinalizeSignupDialogComponent implements OnInit {
  firstName: string;
  lastName: string;
  languageId: string;
  email: string;
  activationCodeId: string;

  activationCodeRetryTimeLeft: number = ACTIVATION_CODE_RETRY_TIME; // Time in seconds for retrying to send the activation code
  interval: NodeJS.Timer | null = null;

  isConfirmButtonActive: boolean = false;
  isSendingActivationCode: boolean = false;
  activationCodeButtonText: string = 'PAGES.SIGNUP.SEND_ACTIVATION_CODE_1';
  codeForm: FormGroup = new FormGroup({});

  codeFormErr = {
    activationCode: '',
  };

  codeFormVal = {
    activationCode: {
      required: 'ERR.FIELD_REQUIRED',
      maxlength: 'ERR.MAX_LENGHT',
    },
  };

  constructor(
    private fb: FormBuilder,
    private dialogData: DynamicDialogConfig,
    private dialogRef: DynamicDialogRef,
    private customerService: CustomerService,
    private toastService: ToastService,
    private translate: TranslateService,
  ) {
    this.firstName = this.dialogData.data.firstName;
    this.lastName = this.dialogData.data.lastName;
    this.languageId = this.dialogData.data.languageId;
    this.email = this.dialogData.data.email;
    this.activationCodeId = this.dialogData.data.activationCodeId;

    this.buildForm();
  }
  ngOnInit(): void {
    this.interval = setInterval(() => {
      if (this.activationCodeRetryTimeLeft > 0) {
        this.activationCodeRetryTimeLeft--;
      } else {
        clearInterval(this.interval);
      }
    }, 1000);

    this.codeForm.valueChanges.pipe(untilDestroyed(this)).subscribe((_) => {
      FormUtils.checkForm(this.codeFormErr, this.codeFormVal, this.codeForm, true, false);
    });

    this.codeForm
      .get('activationCode')!
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((value) => {
        this.isConfirmButtonActive = value != '';
      });
  }

  ngOnDestroy(): void {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  buildForm(): void {
    this.codeForm = this.fb.group({
      activationCode: ['', [Validators.required, Validators.maxLength(10)]],
    });
  }

  /**
   * Send the activation code to customer's email
   */
  async sendActivationCode(): Promise<void> {
    this.isSendingActivationCode = true;

    try {
      const code = await this.customerService.sendEmailActivationCode(this.firstName, this.lastName, this.languageId, this.email);
      this.activationCodeId = code;
      this.codeForm.get('activationCode').setValue('');
      this.toastService.showToast(MessageType.SUCCESS, this.translate.instant('PAGES.SIGNUP.CODE_SENT_TITLE'));
      this.activationCodeRetryTimeLeft = ACTIVATION_CODE_RETRY_TIME; // Reset the retry time to 60 seconds

      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }

      this.interval = setInterval(() => {
        if (this.activationCodeRetryTimeLeft > 0) {
          this.activationCodeRetryTimeLeft--;
        } else {
          clearInterval(this.interval);
        }
      }, 1000);
    } catch (e) {
      console.error(e);
      this.toastService.showErrorToast(e);
    } finally {
      this.isSendingActivationCode = false;
    }
  }

  confirmCode(): void {
    const valid = FormUtils.checkForm(this.codeFormErr, this.codeFormVal, this.codeForm);
    if (!valid) {
      return;
    }

    const result: ActivationCodeData = { activationCodeId: this.activationCodeId, code: this.codeForm.value.activationCode };
    this.dialogRef.close(result);
  }

  getSendActivationCodeLabel() {
    const retryTime = this.activationCodeRetryTimeLeft > 0 ? ` (${this.activationCodeRetryTimeLeft}s)` : '';
    return `${this.translate.instant('PAGES.SIGNUP.SEND_ACTIVATION_CODE')} ${retryTime}`;
  }
}
